/**
 * Swedish localization utilities
 * This file contains utilities for formatting data according to Swedish regional standards
 */

import { formatCurrency } from './formatCurrency';

/**
 * Formats a date according to Swedish standards (YYYY-MM-DD)
 * @param date Date to format
 * @returns Formatted date string
 */
export const formatSwedishDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Formats a date with time according to Swedish standards (YYYY-MM-DD HH:mm)
 * Uses 24-hour clock as per Swedish standards
 * @param date Date to format
 * @returns Formatted date and time string
 */
export const formatSwedishDateTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // Ensure 24-hour format
  });
};

/**
 * Formats a time according to Swedish standards (HH:mm)
 * Uses 24-hour clock as per Swedish standards
 * @param date Date to format
 * @returns Formatted time string
 */
export const formatSwedishTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleTimeString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // Ensure 24-hour format
  });
};

/**
 * Formats a currency amount according to Swedish standards
 * Re-exports the formatCurrency function for completeness
 * @param amount Amount to format
 * @returns Formatted currency string
 */
export const formatSwedishCurrency = formatCurrency;

/**
 * Validates a Swedish postal code
 * Swedish postal codes have the format: NNN NN (3 digits, space, 2 digits)
 * @param postalCode Postal code to validate
 * @returns True if valid, false otherwise
 */
export const isValidSwedishPostalCode = (postalCode: string): boolean => {
  // Match either "XXX XX" or "XXXXX" format
  const regex = /^[0-9]{3}\s?[0-9]{2}$/;
  return regex.test(postalCode);
};

/**
 * Formats a postal code to the Swedish standard format (NNN NN)
 * @param postalCode Postal code to format (can be with or without space)
 * @returns Formatted postal code
 */
export const formatSwedishPostalCode = (postalCode: string): string => {
  // Remove any existing spaces
  const digitsOnly = postalCode.replace(/\s/g, '');
  
  // If we have 5 digits, format as NNN NN
  if (digitsOnly.length === 5) {
    return `${digitsOnly.substring(0, 3)} ${digitsOnly.substring(3, 5)}`;
  }
  
  // Otherwise return as is
  return postalCode;
};

/**
 * Gets the Swedish name for a month
 * @param month Month index (0-11) or Date object
 * @returns Swedish month name
 */
export const getSwedishMonthName = (month: number | Date): string => {
  const monthIndex = month instanceof Date ? month.getMonth() : month;
  
  const months = [
    'januari', 'februari', 'mars', 'april', 'maj', 'juni',
    'juli', 'augusti', 'september', 'oktober', 'november', 'december'
  ];
  
  return months[monthIndex];
};

/**
 * Gets the Swedish name for a day of the week
 * @param day Day index (0-6, where 0 is Sunday) or Date object
 * @returns Swedish day name
 */
export const getSwedishDayName = (day: number | Date): string => {
  const dayIndex = day instanceof Date ? day.getDay() : day;
  
  const days = [
    'söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'
  ];
  
  return days[dayIndex];
};

/**
 * Formats a date in long Swedish format with day and month names
 * Example: "tisdag 15 maj 2023"
 * @param date Date to format
 * @returns Formatted date string with day and month names
 */
export const formatSwedishLongDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const day = dateObj.getDate();
  const month = getSwedishMonthName(dateObj);
  const year = dateObj.getFullYear();
  const weekday = getSwedishDayName(dateObj);
  
  return `${weekday} ${day} ${month} ${year}`;
}; 